import { ArrowLeftOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

interface NewTableKeypadProps {
  onCharacter: (character: string) => void;
  onBackspace: () => void;
  backspaceDisabled: boolean;
  isNarrow: boolean;
}

/** Екранните цифри на „Нова маса": 1–9, тире, 0 и „изтрий знак". Тирето стои
 * долу вляво (там беше „изчисти всичко") — имена като „5-1“ се пишат и без
 * клавиатура; изтрива се знак по знак („изтрий знак“ или Backspace). */
export const NewTableKeypad = ({
  onCharacter,
  onBackspace,
  backspaceDisabled,
  isNarrow,
}: NewTableKeypadProps) => {
  const { t } = useTranslation();
  return (
    <div className={`grid grid-cols-3 ${isNarrow ? "gap-2.5" : "gap-3"}`}>
      {digits.map((digit) => (
        <KeypadButton
          key={digit}
          label={digit}
          onClick={() => onCharacter(digit)}
          isNarrow={isNarrow}
        />
      ))}
      <KeypadButton
        label="-"
        ariaLabel={t("ordering.newTableWaiter.dash")}
        onClick={() => onCharacter("-")}
        isNarrow={isNarrow}
      />
      <KeypadButton label="0" onClick={() => onCharacter("0")} isNarrow={isNarrow} />
      <KeypadButton
        ariaLabel={t("modules.accessModal.backspace") || "Backspace"}
        icon={<ArrowLeftOutlined className="text-[20px]" />}
        onClick={onBackspace}
        disabled={backspaceDisabled}
        isNarrow={isNarrow}
      />
    </div>
  );
};

function KeypadButton({
  label,
  icon,
  ariaLabel,
  onClick,
  disabled,
  isNarrow,
}: {
  label?: string;
  icon?: React.ReactNode;
  ariaLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  isNarrow: boolean;
}) {
  return (
    <motion.button
      type="button"
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.08 }}
      onClick={onClick}
      // Бутонът не взима фокуса — Enter остава за „ОК“, а не за последната цифра.
      onMouseDown={(e) => e.preventDefault()}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`flex w-full cursor-pointer select-none items-center justify-center rounded-[12px] border border-pos-line2 bg-pos-card text-[22px] font-bold text-pos-ink transition-colors duration-[140ms] enabled:hover:border-[color-mix(in_srgb,var(--color-brand)_32%,transparent)] enabled:hover:bg-pos-selected-bg enabled:hover:text-pos-accent-ink disabled:cursor-not-allowed disabled:opacity-40 ${
        isNarrow ? "h-[52px]" : "h-14"
      }`}
    >
      {icon ?? label}
    </motion.button>
  );
}
